import React from "react";
import { Link } from "react-router-dom";
import { Bed, Bath, Maximize, MapPin, Star, Eye, MessageCircle, Heart } from "lucide-react";
import { areaLabel } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";

const TYPE_LABEL = { agricultural: "Agricultural Land", plot: "Plot / Land", apartment: "Apartment", villa: "Villa", commercial: "Commercial" };

export function PropertyCard({ p, index = 0 }) {
  const { siteConfig, user, toggleFavourite } = useAuth();
  const isFav = user?.favourites?.includes(p.id);
  const waLink = siteConfig?.whatsapp
    ? `https://wa.me/${siteConfig.whatsapp}?text=${encodeURIComponent(`Hi Dotlands Realty, I'm interested in "${p.title}" (${p.price_note || p.price_display}) in ${p.locality || p.city}. Please share details.`)}`
    : null;
  return (
    <Link
      to={`/property/${p.id}`}
      data-testid={`property-card-${p.id}`}
      className="group bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 animate-fade-up"
      style={{ animationDelay: `${index * 60}ms` }}
    >
      <div className="relative h-52 overflow-hidden bg-slate-100">
        <img src={p.images?.[0] || "https://images.unsplash.com/photo-1500382017468-9049fed747ef?q=80&w=800"}
          alt={p.title} className={`w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 ${p.status === "sold" ? "grayscale" : ""}`} />
        {p.status === "sold" && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/40">
            <span className="px-4 py-1.5 rounded-md bg-red-600 text-white font-heading font-extrabold tracking-widest -rotate-6 shadow-lg">SOLD OUT</span>
          </div>
        )}
        <div className="absolute top-3 left-3 flex flex-wrap gap-2">
          <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-[#0E3B2E]/90 text-white backdrop-blur">
            {p.status === "sold" ? "Sold" : "For Sale"}
          </span>
          {p.is_verified && (
            <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-emerald-600/95 text-white backdrop-blur flex items-center gap-1" data-testid={`verified-badge-${p.id}`}>
              ✓ Dotlands Verified
            </span>
          )}
        </div>
        {p.featured && (
          <div className="absolute top-0 right-0 z-10 w-28 h-28 overflow-hidden pointer-events-none" data-testid={`boosted-ribbon-${p.id}`}>
            <div className="absolute top-[18px] -right-[34px] rotate-45 w-40 text-center bg-gradient-to-r from-[#E7C56A] via-[#C8A24C] to-[#A8863A] text-[#0E3B2E] text-[11px] font-extrabold tracking-widest py-1 shadow-md flex items-center justify-center gap-1">
              <Star className="w-3 h-3 fill-[#0E3B2E]" /> BOOSTED
            </div>
          </div>
        )}
        <div className="absolute bottom-3 left-3 bg-white/95 backdrop-blur px-3 py-1.5 rounded-lg shadow">
          {p.price_note ? (
            <span className="text-sm font-heading font-extrabold text-[#0E3B2E]">{p.price_note}</span>
          ) : (
            <>
              <span className="text-lg font-heading font-extrabold text-slate-900">{p.price_display}</span>
              {p.price_unit && <span className="text-xs text-slate-500 font-semibold">/{areaLabel(p.price_unit)}</span>}
              {p.listing_type === "rent" && <span className="text-xs text-slate-500">/mo</span>}
            </>
          )}
        </div>
        {user && (
          <button
            data-testid={`fav-btn-${p.id}`}
            aria-label={isFav ? "Remove from saved" : "Save property"}
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleFavourite(p.id); }}
            className="absolute bottom-3 right-3 w-9 h-9 rounded-full bg-white/95 backdrop-blur shadow flex items-center justify-center hover:scale-110 active:scale-95 transition-transform"
          >
            <Heart className={`w-[18px] h-[18px] transition-colors ${isFav ? "fill-red-500 text-red-500" : "text-slate-600"}`} />
          </button>
        )}
      </div>
      <div className="p-4">
        <div className="text-xs font-semibold text-emerald-600 uppercase tracking-wide mb-1">{TYPE_LABEL[p.property_type]}</div>
        <h3 className="font-heading font-semibold text-slate-900 line-clamp-1 group-hover:text-emerald-700 transition-colors">{p.title}</h3>
        <p className="text-sm text-slate-500 flex items-center gap-1 mt-1">
          <MapPin className="w-3.5 h-3.5" /> {p.locality ? `${p.locality}, ` : ""}{p.city}
        </p>
        <div className="flex items-center gap-4 mt-3 pt-3 border-t border-slate-100 text-sm text-slate-600">
          {p.bedrooms > 0 && <span className="flex items-center gap-1"><Bed className="w-4 h-4" /> {p.bedrooms}</span>}
          {p.bathrooms > 0 && <span className="flex items-center gap-1"><Bath className="w-4 h-4" /> {p.bathrooms}</span>}
          <span className="flex items-center gap-1"><Maximize className="w-4 h-4" /> {p.area} {areaLabel(p.area_unit)}</span>
          <span className="flex items-center gap-1 ml-auto text-slate-400 text-xs"><Eye className="w-3.5 h-3.5" /> {p.views}</span>
        </div>
        {waLink && (
          <button
            data-testid={`whatsapp-btn-${p.id}`}
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); window.open(waLink, "_blank"); }}
            className="mt-3 w-full flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold py-2 rounded-lg transition-colors"
          >
            <MessageCircle className="w-4 h-4" /> WhatsApp Dotlands
          </button>
        )}
      </div>
    </Link>
  );
}
